import * as Tone from "tone";
import useMutatableInternalState from "./useMutatableInternalState";
import useToneLoop from "./useToneLoop";
import useSynths from "./useSynths";
import { logger } from "./logger";
import useExternalState, {
  PlayerExternalState,
} from "./useExternalState/useExternalState";
import { useCallback } from "react";
import useSyncBpmWithExternalState from "./useSyncBpmWithExternalState";
import { Range } from "../constants/ranges";
import { useSearchParams } from "next/navigation";
import getDefaultExternalState, {
  ExternalStateSearchParams,
  searchParamsToDefaultProps as searchParamsToDefaultParams,
} from "./getDefaultExternalState";
import api, { isSuccess } from "util/api";
import {
  ApiPostSingingPracticeGetShortLinkParams,
  apiPostSingingPracticeGetShortLinkPath,
  ApiPostSingingPracticeGetShortLinkResponse,
} from "apiRoutes/apiSingingPracticeRoutes";
import { playSilentAudio } from "util/playSilentAudio";
import convertMelodyRangeScaleToInternalState from "./useExternalState/convertMelodyRangeScaleToInternalState";
import useNoSleep from "./useNoSleep";
import { storeUserRangeInStorage } from "./storeUserRange";
import { VocalRegister } from "../constants/vocalRegister";

export type TranspositionPattern = "up/down" | "random";

export type Player = ReturnType<typeof usePlayer>;

interface Props {
  defaultState?: Partial<PlayerExternalState>;
}

export default function usePlayer(props: Props) {
  const noSleep = useNoSleep();
  const searchParams = useSearchParams();
  const defaultState = getDefaultExternalState(
    searchParamsToDefaultParams(searchParams),
    props.defaultState
  );

  const { getInternalState, mutateInternalState } =
    useMutatableInternalState(defaultState);
  const [externalState, setExternalState] = useExternalState({
    defaultState,
    mutateInternalState,
    onStop() {
      Tone.Draw.cancel();
      synth.releaseAll();
    },
  });
  const synth = useSynths({ state: externalState });

  useToneLoop({
    getInternalState,

    updateExternalState(state) {
      logger("UPDATE EXTERNAL STATE");

      setExternalState((prev) => ({
        ...prev,
        ...state,
      }));
    },

    triggerAttackRelease: synth.triggerAttackRelease,
  });

  useSyncBpmWithExternalState({ state: externalState });

  const stop = useCallback(() => {
    Tone.Transport.pause();
    noSleep?.enable();
  }, [noSleep]);

  const setBpm = useCallback(
    (bpm: number) => {
      setExternalState((prev) => ({
        ...prev,
        bpm,
      }));
      Tone.Transport.bpm.value = bpm;
    },
    [setExternalState]
  );

  const setVolume = useCallback(
    (type: keyof PlayerExternalState["volume"], value: number) => {
      setExternalState((prev) => ({
        ...prev,
        volume: {
          ...prev.volume,
          [type]: value,
        },
      }));
    },
    [setExternalState]
  );

  const setExternalStateAndMutateInternalState = useCallback(
    (state: Partial<PlayerExternalState>) => {
      setExternalState((prev) => {
        const nextState = {
          ...prev,
          ...state,
        };

        const internalState = convertMelodyRangeScaleToInternalState(nextState);

        nextState.activeScale = internalState.scales[internalState.scaleIndex];

        // dk how good of an idea this is, but the internal state should prob be updated before the external state is updated
        mutateInternalState(internalState);

        return nextState;
      });
    },
    [setExternalState, mutateInternalState]
  );

  const setMelodyShorthand = useCallback(
    (shorthand: string) => {
      setExternalStateAndMutateInternalState({
        shorthands: {
          melody: shorthand,
        },
      });
    },
    [setExternalStateAndMutateInternalState]
  );

  const setRange = useCallback(
    (range: Range) => {
      setExternalStateAndMutateInternalState({
        range,
      });
      storeUserRangeInStorage(range);
    },
    [setExternalStateAndMutateInternalState]
  );

  const setVocalRegister = useCallback(
    (register: VocalRegister) => {
      setExternalStateAndMutateInternalState({
        vocalRegister: register,
      });
    },
    [setExternalStateAndMutateInternalState]
  );

  const enabled =
    !!externalState.range && !!externalState.shorthands?.melody && synth.loaded;

  return {
    enabled: {
      breakdown: {
        range: !!externalState.range,
        melodyShorthand: !!externalState.shorthands?.melody,
        loaded: synth.loaded,
      },

      enabled,
    },

    state: externalState,

    toggleHoldScale() {
      setExternalState((prev) => ({
        ...prev,
        holdScale: !prev.holdScale,
      }));
    },

    setTranspositionPattern(pattern: TranspositionPattern) {
      setExternalState((prev) => ({
        ...prev,
        // stop holding the scale when the pattern changes
        holdScale: false,
        transpositionPattern: pattern,
      }));
    },
    start() {
      if (!enabled) {
        logger("start: not enabled");

        return;
      }

      logger(
        "REQUEST START",
        Tone.Transport.now(),
        Tone.Transport.state,
        Tone.context.state
      );
      noSleep?.enable();
      playSilentAudio();
      Tone.start();
      Tone.context.resume();
      Tone.Transport.start();
    },
    stop,
    playMelody: synth.playMelody,
    setVolume,
    setBpm,
    setMelodyShorthand,
    setRange,
    setVocalRegister,
    loaded: synth.loaded,

    async submitShareLink(slug: string): Promise<string | null> {
      if (!enabled) {
        logger("submitShareLink: not enabled");

        return Promise.resolve(null);
      }

      // Only for ts — enabled should cover it
      if (!externalState.range) {
        logger("submitShareLink: no range");

        return Promise.resolve(null);
      }

      // Only for ts — enabled should cover it
      if (!externalState.shorthands) {
        logger("submitShareLink: no shorthands");

        return Promise.resolve(null);
      }

      const value = await api<
        ApiPostSingingPracticeGetShortLinkResponse,
        ApiPostSingingPracticeGetShortLinkParams
      >(apiPostSingingPracticeGetShortLinkPath, "POST", {
        body: {
          slug,
          [ExternalStateSearchParams.Bpm]: externalState.bpm.toString(),
          [ExternalStateSearchParams.Range]: externalState.range.range,
          [ExternalStateSearchParams.RangeStart]: externalState.range.low,
          [ExternalStateSearchParams.RangeEnd]: externalState.range.high,
          [ExternalStateSearchParams.ShorthandMelody]:
            externalState.shorthands.melody,
          [ExternalStateSearchParams.VocalRegister]:
            externalState.vocalRegister,
        },
      });

      if (isSuccess(value)) {
        return value.response.short_link;
      }

      return null;
    },
  };
}
